import store from '@/store'
import { postDC, postPaas } from '@/assets/utils/request'
import {
  getCachedWebposInfo,
  getWebposToken,
  isWebposHost,
  markWebposSessionActive,
  normalizeWebposInfo,
  saveCachedWebposInfo,
} from '@/utils/webpos-storage'

const WEBPOS_DECRYPT_ACTION = 'candao.member.getTokenDetailForWebPOS'

let initPromise = null
let initializedToken = ''

const getResponseData = (response) => response?.data || response || {}

const applyWebposLanguage = (language) => {
  if (!language) {
    return
  }
  store.commit('setLanguage', String(language).toUpperCase() === 'E' ? 2 : 1)
}

export const decryptWebposToken = async (token) => {
  const params = {
    actionName: WEBPOS_DECRYPT_ACTION,
    content: {
      token,
    },
  }
  const result = await postPaas('UserUnify', params)
  if (result?.status !== 1) {
    throw new Error(result?.msg || 'WebPOS token decrypt failed')
  }
  return getResponseData(result)
}

export const loginWebposByAccessCode = async (accessCode) => {
  const profileParams = {
    actionName: 'candao.member.userProfileGet',
    content: {
      accessCode,
    },
  }
  const profileResult = await postPaas('UserUnify', profileParams)
  if (profileResult?.status !== 1) {
    throw new Error(profileResult?.msg || 'WebPOS profile get failed')
  }

  const profile = getResponseData(profileResult)
  const phone = profile.phoneNo || profile.phone || ''
  const countryCode = profile.areaCode || profile.countryCode || (store.state.area == 1 ? '+853' : '+852')

  await postDC('Action', {
    actionName: 'candao.user.logout',
    content: {},
  })
  store.commit('clearUserInfo')
  store.commit('clearCart')

  const userResult = await postDC('Action', {
    actionName: 'candao.user.getUserByExtUserId',
    content: {
      extUserId: profile.userId,
      phone,
      countryCode,
    },
  })
  if (userResult?.status !== 1) {
    throw new Error(userResult?.msg || 'WebPOS get user failed')
  }

  const bindResult = await postPaas('UserUnify', {
    actionName: 'candao.member.bindAccessCode',
    secretKey: userResult.data.secretKey,
    content: {
      accessCode,
    },
  })
  if (bindResult?.status !== 1) {
    throw new Error(bindResult?.msg || 'WebPOS bind accessCode failed')
  }

  const finalProfileResult = await postPaas('UserUnify', profileParams)
  if (finalProfileResult?.status !== 1) {
    throw new Error(finalProfileResult?.msg || 'WebPOS final profile get failed')
  }

  const userInfo = getResponseData(finalProfileResult)
  userInfo.accessCode = accessCode
  store.commit('setUserInfo', userInfo)
  localStorage.setItem('store', JSON.stringify(store.state))
  return userInfo
}

const doInitWebposSession = async (token) => {
  const cachedInfo = getCachedWebposInfo()
  if (initializedToken === token && cachedInfo?.token === token && cachedInfo?.deviceId) {
    store.commit('saveDeviceId', cachedInfo.deviceId)
    applyWebposLanguage(cachedInfo.language)
    markWebposSessionActive()
    return cachedInfo
  }

  const decryptData = await decryptWebposToken(token)
  const webposInfo = normalizeWebposInfo({
    ...decryptData,
    token,
  })
  if (!webposInfo.deviceId) {
    throw new Error('WebPOS deviceId is empty')
  }

  saveCachedWebposInfo(webposInfo)
  markWebposSessionActive()
  store.commit('saveDeviceId', webposInfo.deviceId)
  applyWebposLanguage(webposInfo.language)

  if (webposInfo.accessCode) {
    await loginWebposByAccessCode(webposInfo.accessCode)
  } else {
    store.commit('clearUserInfo')
  }

  initializedToken = token
  return webposInfo
}

export const initWebposSession = async (token = getWebposToken()) => {
  if (!isWebposHost()) {
    return null
  }
  if (!token) {
    throw new Error('WebPOS token is empty')
  }
  if (!initPromise) {
    initPromise = doInitWebposSession(token).finally(() => {
      initPromise = null
    })
  }
  return initPromise
}

export { getWebposToken, isWebposHost }
